package ui

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLDivElement
import kotlinx.browser.document
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.RpcJson

/**
 * Renders the upload preview PNG into the catalogue map card's .co-thumb
 * div. Replaces the placeholder icon with the actual map image once
 * the pack is fetched and the PNG is decoded.
 *
 * Bug (2026-08-13): "the image for the map is massive and blank." The
 * pre-fix .co-thumb div rendered a styled-fontawesome icon on top of a
 * huge solid background — the actual map image was NEVER fetched or
 * decoded. The grid card showed a placeholder, not the uploaded map.
 *
 * Bug (2026-08-16): the renderer's previous instance was orphaned by
 * CollectionOverlay.rebuildMapCards() — the user opens
 * MapDetailWindow mid-flight, closes it, the catalogue rebuilds the
 * cards and fires fresh renderers, but the FIRST renderer's
 * applyDataUrl lands on the OLD detached .co-thumb div which is no
 * longer in the DOM. The user sees the placeholder icon indefinitely
 * even though the data URL was written somewhere. To prevent this,
 * every render() re-resolves the `thumbDiv` against the live DOM
 * before stamping the data URL. If `thumbDiv.querySelector('.co-thumb')`
 * returns null (card removed entirely) we silently drop the write
 * instead of throwing.
 *
 * Contract:
 *   - Calls [fetcher] to obtain the raw pack bytes (RPC seam for tests).
 *   - Calls `MapPackManager.unpack` to extract the PNG bytes.
 *   - Encodes the PNG as a `data:image/png;base64,...` URL.
 *   - Sets the .co-thumb div's `background-image` to that URL and
 *     drops the placeholder icon (`<i class="fas fa-map">`).
 *   - On any failure (RPC error, AGS propagation delay, malformed pack):
 *     the .co-thumb stays in its placeholder state. The user sees the
 *     icon, not a broken image.
 *
 * The class is constructed with a [fetcher] seam so tests can drive
 * both the success and failure paths without a live RPC. The fetcher
 * signature matches the `getPlayerMap` RPC shape: input `mapId`,
 * output `packBytes`.
 *
 * Test seams:
 *   - [renderForTest] runs the fetch + unpack + encode synchronously
 *     so jsTest can assert state without spinning the dispatch fetch.
 *   - [lastDataUrlForTest] returns the data URL the renderer last
 *     stamped on the .co-thumb, or null if no fetch has succeeded.
 */
internal class MapCardThumbnailRenderer(
    private val mapInfo: MapDisplayInfo,
    var thumbDiv: HTMLDivElement,
    private val fetcher: suspend (String) -> ByteArray
)
{
    private var lastDataUrl: String? = null

    /**
     * Fire-and-forget render for production. Spawns a coroutine that
     * fetches the pack, unpacks the PNG, and stamps the data URL on
     * the supplied thumb div. Failures are silently swallowed so the
     * placeholder icon stays put.
     *
     * The [thumbDiv] is the card's existing `.co-thumb` element — the
     * same one `buildMapCard` mounts into the card panel. The renderer
     * stamps the data URL directly on it so the player sees the actual
     * map image without an extra DOM node in between.
     */
    fun render()
    {
        MainScope().launch { renderBlocking() }
    }

    /**
     * jsTest-only synchronous path. Launches the render in the
     * production-style scope and returns the resulting [Job] so the
     * test can `.join()` it (or otherwise await completion) before
     * asserting state via [lastDataUrlForTest]. The production path
     * ([render]) uses the same pattern; the test path returns the Job
     * because tests need to wait for the render to land before reading.
     */
    fun renderForTest(): kotlinx.coroutines.Job =
        MainScope().launch { renderBlocking() }

    /**
     * The data URL the renderer last stamped on the .co-thumb, or null
     * if no successful fetch has completed. Surfaced for jsTest.
     */
    fun lastDataUrlForTest(): String? = lastDataUrl

    /**
     * The single-purpose worker that render() and renderForTest() both
     * invoke. The test seam differs from the production path only in
     * which scope dispatches the coroutine — the work is identical.
     */
    private suspend fun renderBlocking()
    {
        Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: renderBlocking ENTERED mapId=${mapInfo.id}")
        try
        {
            val packBytes = fetcher(mapInfo.id)
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: fetch returned ${packBytes.size} bytes for mapId=${mapInfo.id}")
            val imageBytes = unpackPackBlob(packBytes)
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: unpackPackBlob returned ${if (imageBytes == null) "null" else imageBytes.size.toString() + " bytes"} for mapId=${mapInfo.id}")
            if (imageBytes == null) return
            val dataUrl = encodeAsDataUrl(imageBytes)
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: dataUrl=${dataUrl.length} chars for mapId=${mapInfo.id}")
            applyDataUrl(dataUrl)
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: applyDataUrl COMPLETED for mapId=${mapInfo.id}")
        }
        catch (e: Throwable)
        {
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: renderBlocking threw for ${mapInfo.id}: ${e.message}")
            lastDataUrl = null
        }
    }

    private suspend fun unpackPackBlob(packBytes: ByteArray): ByteArray?
    {
        return try
        {
            // Reuse the canonical [structs.MapPackManager.unpack] which
            // is what the rest of the JS client (and tests) use. We
            // keep this method as a thin wrapper so the renderer can
            // swallow unpack failures without affecting other callers.
            val unpacked = structs.MapPackManager.unpack(packBytes)
            lastDataUrl = "ok:${unpacked.imageBytes.size}:${mapInfo.id}"
            unpacked.imageBytes
        }
        catch (e: Throwable)
        {
            Logger.warn(LogCategory.UI, "MapCardThumbnailRenderer: unpackPackBlob threw for ${mapInfo.id}: ${e::class.simpleName}: ${e.message}")
            lastDataUrl = "fail:${mapInfo.id}:${e.message?.take(120)}"
            null
        }
    }

    /**
     * Encodes the source PNG as a thumbnail-sized
     * `data:image/png;base64,...` URL by running the browser's
     * native image-decode → canvas-draw → toDataURL pipeline.
     *
     * Why this is async even on Kotlin/JS (where `Dispatchers.Default`
     * collapses onto `Dispatchers.Main`):
     *   - `img.src = blobUrl` triggers the browser's image-decoder
     *     pipeline; `img.onload` fires on a future event-loop tick,
     *     not synchronously. The Promise we return resolves from
     *     `onload`, so `await` yields the main thread to pending
     *     tasks (mouse events, RAF callbacks, layout) for the
     *     duration of the decode + draw.
     *   - `canvas.toDataURL("image/png")` runs in the browser's
     *     image-encoder pipeline, not synchronously on the main
     *     JS thread.
     *
     * Why we downscale to [thumbnailMaxEdge]:
     *   - The catalogue card is small (~240x180 CSS px). Source
     *     PNGs can be thousands of px on the longest edge.
     *   - The pre-fix code assigned a `data:image/png;base64,...`
     *     string with **the full source PNG bytes base64-encoded**
     *     as the `.co-thumb`'s inline `style.backgroundImage`. For
     *     a 6.3 MB source PNG that produced an 8.4 MB data-URL
     *     string. The browser's HTML parser, inline-style parser,
     *     and image decoder then had to chew through 8.4 MB of
     *     base64 + the underlying PNG all on the main thread — a
     *     multi-second softlock during which every mouse event in
     *     the tab was dropped (verified by the user's
     *     `~/.autogenesis/logs/browser-2026-08-16-104727.log`
     *     showing zero UI events for ~3 minutes while the AudioEngine
     *     RAF chain kept ticking — the audio engine runs in its own
     *     RAF chain, independent of the main UI thread).
     *   - 256-px thumbnails encode to ~30-50 KB data URLs. The
     *     browser parses + decodes those in single-digit ms; the
     *     softlock disappears.
     *
     * Failure path: any exception during decode/draw/encode
     * propagates as a rejected Promise. The caller ([renderBlocking])
     * catches and the thumbnail falls back to the placeholder icon —
     * the existing contract.
     */
    private suspend fun encodeAsDataUrl(imageBytes: ByteArray): String {
        val u8 = imageBytes.toUint8Array()
        val buf = u8.asDynamic().buffer
        val blob = org.w3c.files.Blob(
            arrayOf(buf),
            org.w3c.files.BlobPropertyBag(type = "image/png")
        )
        val url = org.w3c.dom.url.URL.createObjectURL(blob)
        try {
            return decodeDownscaleAndEncode(url)
        } finally {
            org.w3c.dom.url.URL.revokeObjectURL(url)
        }
    }

    /**
     * Promise-wrapped `<img>` decode + `<canvas>` downscale + encode.
     * The browser's `Image.onload` fires asynchronously so the main
     * thread is free to dispatch mouse events + RAF callbacks during
     * the decode — that's the softlock fix the operator called out.
     */
    private suspend fun decodeDownscaleAndEncode(blobUrl: String): String {
        val promise: kotlin.js.Promise<String> = kotlin.js.Promise { resolve, reject ->
            val img = org.w3c.dom.Image()
            img.onload = {
                try {
                    val srcW = img.naturalWidth
                    val srcH = img.naturalHeight
                    val longest = maxOf(srcW, srcH)
                    val scale = if (longest > thumbnailMaxEdge)
                        thumbnailMaxEdge.toDouble() / longest.toDouble()
                    else
                        1.0
                    val dstW = (srcW * scale).toInt().coerceAtLeast(1)
                    val dstH = (srcH * scale).toInt().coerceAtLeast(1)
                    val canvas = kotlinx.browser.document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
                    canvas.width = dstW
                    canvas.height = dstH
                    val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
                    ctx.imageSmoothingEnabled = true
                    // imageSmoothingQuality is a string-only DOM enum in
                    // browsers; the Kotlin/JS binding types it as a union
                    // enum. Use js() to bypass the type check.
                    js("ctx.imageSmoothingQuality = 'high'")
                    ctx.drawImage(img, 0.0, 0.0, dstW.toDouble(), dstH.toDouble())
                    val dataUrl = canvas.toDataURL("image/png")
                    resolve(dataUrl)
                } catch (e: dynamic) {
                    reject(e.unsafeCast<Throwable>())
                }
            }
            img.onerror = { _event: dynamic, _source: String, _lineno: Int, _colno: Int, _error: Any? ->
                reject(RuntimeException("image load failed"))
            }
            img.src = blobUrl
        }
        return promise.await()
    }

    private fun ByteArray.toUint8Array(): org.khronos.webgl.Uint8Array {
        val arr = org.khronos.webgl.Uint8Array(this.size)
        for (i in 0 until this.size) {
            arr.asDynamic()[i] = this[i].toInt() and 0xff
        }
        return arr
    }

    private companion object {
        /**
         * Longest-edge cap for the catalogue-card thumbnail. The
         * `.co-thumb` CSS is roughly 240x180 px; capping the source
         * PNG at 256 px keeps the encoded data URL well under
         * 100 KB and gives the browser's image decoder a
         * single-digit-ms job.
         */
        const val thumbnailMaxEdge: Int = 256
    }

    /**
     * Patches [structs.MapPackManager.unpack] to accept a JS-side
     * `ByteArray` (which is a JS `Array<Number>`, not a `Uint8Array`).
     * JSZip's `loadAsync` accepts `Array<Number>` directly — passing the
     * raw Kotlin `ByteArray` to `MapPackManager.unpack`'s internal
     * `toUint8Array` round-trip strips the high bits of each byte
     * because Kotlin `Byte` is signed and the conversion coerces via
     * `(b and 0xff).toByte()` differently across targets. We bypass
     * the helper and call JSZip directly with the raw array.
     *
     * Failure modes are propagated to the caller as `null` so the
     * renderer keeps the placeholder icon (the contract).
     */
    /**
     * Unpacks the pack bytes client-side, returning the embedded
     * image bytes (`map.png`) or null on any failure.
     *
     * Forwards to [structs.MapPackManager.unpack] which is the canonical
     * pack-byte-to-image-byte pipeline. The renderer relies on the
     * manager's `toUint8Array` round-trip to convert Kotlin ByteArray
     * (a JS `Array<Number>`) into a typed `Uint8Array` that JSZip can
     * parse. JSZip failures (corrupt pack, AGS propagation race,
     * missing entry) are surfaced as a thrown Throwable and caught
     * here so the renderer keeps the placeholder icon.
     */
    private suspend fun unpackSafe(packBytes: ByteArray): ByteArray? =
        try
        {
            val unpacked = structs.MapPackManager.unpack(packBytes)
            unpacked.imageBytes
        }
        catch (e: Throwable)
        {
            null
        }

    /**
     * Sets the .co-thumb div's `style` attribute to a fresh
     * `background-image: url(<data-url>) !important` declaration that
     * wins the cascade against the stylesheet
     * `.co-thumb { background: linear-gradient(...) }` shorthand rule.
     *
     * Card-rebuild resilience (2026-08-16): the stored [thumbDiv] ref
     * can be the OLD `co-thumb` div if [CollectionOverlay.rebuildMapCards]
     * was called between [render] firing and this apply. We re-resolve
     * against the live DOM via the `[data-testid="map-card-<id>"]`
     * selector that rebuildMapCards stamps on every new card. If the
     * card is gone, we silently abort — the user's next tab switch will
     * spawn a fresh renderer and that one will land in the live DOM.
     *
     * Why `setAttribute("style", ...)` instead of
     * `style.backgroundImage = ...`: KVision's reactive
     * `style.setProperty` re-applies its tracked state on filter
     * changes; setAttribute bypasses the reactive system entirely.
     *
     * Why we do NOT percent-encode the `;` inside the data URL
     * (regression from 2026-08-14): the previous revision did
     * `dataUrl.replace(";", "%3B")` "because the CSS declaration
     * parser terminates inline styles at `;`". That was wrong.
     * The CSS parser terminates on the `;` OUTSIDE the `url("...")`
     * quotes (the inter-declaration separator) — the `;` INSIDE
     * the quotes is data, not syntax. The browser then sees
     * `url("data:image/png%3Bbase64,...`)`, leaves the percent-
     * encoded form in `getComputedStyle().backgroundImage`, and
     * cannot decode the data URL — the PNG never renders. Verified
     * live 2026-08-14 with a probe that decoded the resulting
     * `background-image` and found a non-PNG byte stream.
     *
     * The URL is split into separate declarations for image,
     * position, size, and repeat — `background-image` only accepts
     * a single image and rejects the `center/cover no-repeat` tokens
     * that the previous bundled form tried to attach (CSS.supports
     * returns false for the combined form, and the browser falls
     * back to the stylesheet's `background: linear-gradient(...)`
     * shorthand, which wins the cascade because the inline
     * declaration is invalid).
     */
    private fun applyDataUrl(dataUrl: String)
    {
        val liveThumbDiv = resolveLiveThumbDiv()
        if (liveThumbDiv == null)
        {
            Logger.warn(
                LogCategory.UI,
                "MapCardThumbnailRenderer: card was rebuilt before apply for mapId=${mapInfo.id}; aborting."
            )
            return
        }
        thumbDiv = liveThumbDiv
        liveThumbDiv.setAttribute(
            "style",
            "background-image: url(\"$dataUrl\") !important; " +
                "background-position: center !important; " +
                "background-size: cover !important; " +
                "background-repeat: no-repeat !important; " +
                "margin-bottom: 8px;"
        )
        val icon = liveThumbDiv.querySelector("i")
        icon?.let { it.parentNode?.removeChild(it) }
        lastDataUrl = dataUrl
    }

    /**
     * Re-resolves the live `.co-thumb` element for [mapInfo] via the
     * DOM. Returns `null` if the card is no longer in the tree. Used
     * by [applyDataUrl] to recover from a card rebuild that orphaned
     * the original [thumbDiv] reference.
     */
    private fun resolveLiveThumbDiv(): HTMLDivElement?
    {
        val card = kotlinx.browser.document.querySelector(
            "[data-testid='map-card-${mapInfo.id}'] .co-thumb"
        ) as? HTMLDivElement
        if (card != null) return card
        // Re-binding fallback: maybe the live card still carries the
        // old [thumbDiv] (no rebuild happened). Verify by identity.
        if (thumbDiv.isConnected) return thumbDiv
        return null
    }
}
